import React, { useState } from 'react';
import cn from 'classnames';
import { ChevronDown } from 'react-feather';
import useFetch from 'components/shared/hooks/useFetch';
import { ActionList, Popover } from 'ui';
import Text, { TEXT_VARIANTS } from 'components/ui/Typography/Text';
import Tooltip from 'components/shared/Tooltip';
import { routes } from 'constants/config';

/**
 * Displays a company switcher for an organization.
 *
 * Doesn't render if the user only has one account in the organization.
 */
const CompanySwitcher = ({ expandedView = false }) => {
  const { data, loading, error } = useFetch(routes.ORGANIZATIONS.LINKED_USERS);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const buttonClasses = cn(
    'CompanySwitcher-button',
    'border-midnight-30 border-solid c-midnight-100 fz-bigger',
    'bg-transparent d-f bs-n bdrs-4 ai-c minw-150 jc-sb',
    {
      'border-midnight-100': popoverOpen,
    },
  );

  if (loading || error) {
    return null;
  }

  // Don't show if the user only has one account.
  if (data.linked_accounts.length < 2) {
    return null;
  }

  const currentAccount = data.linked_accounts.find(account => account.active);
  const otherAccounts = data.linked_accounts
    .filter(account => !account.active)
    .sort((account1, account2) => account1.name.localeCompare(account2.name));

  return (
    <div>
      <Popover
        isHighPriority
        visible={popoverOpen}
        onClose={() => setPopoverOpen(false)}
        className={cn('CompanySwitcher-popover', { expandedView })}
        activator={
          <Tooltip
            className={cn({ 'd-n': popoverOpen })}
            text={t('navbar.company_switcher.tooltip')}
            align="start"
          >
            <button
              type="button"
              onClick={() => setPopoverOpen(!popoverOpen)}
              className={buttonClasses}
              aria-label={t('navbar.company_switcher.button_label')}
            >
              <div className="d-f fd-c ai-fs ov-h">
                <Text
                  variant={TEXT_VARIANTS.INSTRUX_SMALL_SEMIBOLD}
                  className="CompanySwitcher-viewingLabel"
                >
                  {t('navbar.company_switcher.currently_viewing')}
                </Text>
                <Text
                  variant={TEXT_VARIANTS.BODY}
                  className="CompanySwitcher-accountName d-b ov-h to-e ws-nw w-fill"
                  title={currentAccount?.name}
                  data-test-id="CompanySwitcher-accountName"
                >
                  {currentAccount?.name ||
                    t('navbar.company_switcher.button_label')}
                </Text>
              </div>
              <ChevronDown size={16} className="ml-16 f-sh-0" />
            </button>
          </Tooltip>
        }
      >
        <div className="bdrs-4 f-o-n bg-white pt-8 pb-6 bs-3 minw-150">
          {currentAccount && (
            <div className="CompanySwitcher-currentAccount border-bottom border-midnight-30 px-16 pb-8">
              <Text variant={TEXT_VARIANTS.INSTRUX_SMALL_SEMIBOLD}>
                {t('navbar.company_switcher.currently_viewing')}
              </Text>
              <Text
                variant={TEXT_VARIANTS.BODY_BOLD}
                className="d-b ov-h to-e ws-nw w-fill"
                title={currentAccount.name}
              >
                {currentAccount.name}
              </Text>
            </div>
          )}

          <ActionList
            items={otherAccounts.map(account => ({
              content: (
                <Text
                  variant={TEXT_VARIANTS.BODY_LIGHT}
                  className="d-b ov-h to-e ws-nw w-fill"
                  title={account.name}
                >
                  {account.name}
                </Text>
              ),
              url: account.url,
            }))}
          />
        </div>
      </Popover>
    </div>
  );
};

export default props => <CompanySwitcher {...props} />;
